import type { TrackProps } from './TrackContainer'
import TrackContainer from './TrackContainer'
import MediaFileClip from './Clips/MediaFileClip'
import useTracksStore, {
  trackSelector,
  TrackType,
} from '../../../shared/store/tracks.store.tsx'
import { memo, useEffect, useState, type FC } from 'react'
import useUiStore from '../../../shared/store/ui.store.tsx'
import Icon from '../../common/Icon.tsx'
import { SidebarType } from '../../Sidebar/Sidebar.tsx'
import { useShallow } from 'zustand/react/shallow'
import { addClip } from '../../../features/clips/domain/use-cases/add-clip.ts'

const MediaFileTrack: FC<TrackProps> = ({ trackId }) => {
  const track = useTracksStore(trackSelector(trackId))
  const { draggedClip, setDraggedClip, getAvailablePosition } = useTracksStore(
    useShallow((state) => ({
      draggedClip: state.draggedClip,
      setDraggedClip: state.setDraggedClip,
      getAvailablePosition: state.getAvailablePosition,
    }))
  )
  const { setSidebarType, pixelsPerSecond } = useUiStore(
    useShallow((state) => ({
      pixelsPerSecond: state.pixelsPerSecond,
      setSidebarType: state.setSidebarType,
    }))
  )
  const [containerElement, setContainerElement] =
    useState<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!draggedClip || draggedClip.type !== TrackType.MediaFiles) return

    const handlePointerMove = (e: PointerEvent) => {
      if (!containerElement) return

      const rect = containerElement.getBoundingClientRect()

      const isInside =
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom

      if (
        !isInside &&
        draggedClip.startAt === undefined &&
        draggedClip?.trackId !== undefined &&
        draggedClip.trackId !== trackId
      )
        return

      const position = e.clientX - rect.left
      const dragXPosition = Math.max(0, position - 24)

      if (isInside) {
        const newStartAt = getAvailablePosition(
          {
            ...draggedClip,
            startAt: dragXPosition / pixelsPerSecond,
          },
          8 / pixelsPerSecond,
          trackId
        )

        const isForbiddenPosition =
          dragXPosition > 0 && newStartAt === undefined

        if (isForbiddenPosition) {
          setDraggedClip({
            ...draggedClip,
            trackId,
            startAt: dragXPosition / pixelsPerSecond,
            isOverlapping: true,
          })
          return
        }

        if (
          draggedClip.trackId !== trackId ||
          draggedClip.startAt !== newStartAt
        ) {
          setDraggedClip({
            ...draggedClip,
            trackId,
            startAt: newStartAt,
            isOverlapping: false,
          })
        }
      } else if (
        draggedClip.trackId === trackId &&
        draggedClip.startAt !== undefined
      ) {
        setDraggedClip({
          ...draggedClip,
          trackId: 'tmp',
        })
      }
    }

    const handlePointerUp = () => {
      if (draggedClip) {
        if (!draggedClip.isOverlapping) {
          // Check if clip already exists in the track (moving existing clip)
          const existingClip = track.clips.find((c) => c.id === draggedClip.id)
          if (!existingClip) {
            // Only add if it's a new clip (dragged from sidebar)
            addClip(draggedClip)
          }
          // If it exists, ClipContainer already handles the position update
        }
        setDraggedClip(undefined)
      }
    }

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)

    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
    }
  }, [draggedClip, containerElement, setDraggedClip, trackId])

  return (
    <div className="flex">
      <div className="h-[134px] min-w-4 border-b border-b-gray-800" />
      <TrackContainer
        ref={setContainerElement}
        contextMenuVM={[
          {
            key: 'ls_track_context_menu_add_media_file',
            action: () => setSidebarType(SidebarType.MediaFiles),
            icon: <Icon name="mediaFile" />,
          },
        ]}
      >
        <div className="relative h-full w-full">
          {track.clips
            .filter((c) => c.startAt !== undefined)
            .map((clip) => (
              <MediaFileClip key={clip.id} clip={clip} />
            ))}
          {draggedClip?.trackId === trackId && (
            <MediaFileClip clip={draggedClip} />
          )}
        </div>
      </TrackContainer>
      <div className="h-[134px] w-4 border-b border-b-gray-800" />
    </div>
  )
}

export default memo(MediaFileTrack)
